import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";

import { getNoteMailSetting, saveNoteMailSetting } from "../../../reducer/studentResearch/action";

// NoteMailSettingDialog — Phòng QLKH bật/tắt + chỉnh mẫu email gửi GV hướng
// dẫn khi cập nhật kết quả cấp Khoa / Trường / Quốc gia / Quốc tế có ghi chú.
// Mẫu là văn bản thuần; bấm vào biến để chèn vào ô đang chọn.
export default function NoteMailSettingDialog({ open, onClose }) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [placeholders, setPlaceholders] = useState([]);
  const [focused, setFocused] = useState("content");
  const subjectRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    dispatch(getNoteMailSetting()).then((data) => {
      if (!active) return;
      if (data) {
        setEnabled(!!data.setting?.enabled);
        setSubject(data.setting?.subject || "");
        setContent(data.setting?.content || "");
        setPlaceholders(data.placeholders || []);
      }
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [open, dispatch]);

  const insertPlaceholder = (key) => {
    const isSubject = focused === "subject";
    const el = (isSubject ? subjectRef : contentRef).current;
    const value = isSubject ? subject : content;
    const setValue = isSubject ? setSubject : setContent;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    setValue(value.slice(0, start) + key + value.slice(end));
    setTimeout(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(start + key.length, start + key.length);
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const ok = await dispatch(saveNoteMailSetting({ enabled, subject, content }));
      if (ok) onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Cấu hình email ghi chú kết quả các cấp</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" padding={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Alert severity="info" style={{ marginBottom: 16 }}>
              Khi Phòng QLKH cập nhật kết quả cấp Khoa, cấp Trường, cấp Bộ/Quốc gia, cấp Quốc tế
              và có nhập <b>Ghi chú</b>, hệ thống sẽ tự động gửi email theo mẫu dưới đây tới
              giảng viên hướng dẫn của đề tài.
            </Alert>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                />
              }
              label={enabled ? "Đang bật tự động gửi email" : "Đang tắt tự động gửi email"}
            />

            <Box marginTop={2}>
              <Typography variant="body2" style={{ marginBottom: 6 }}>
                Biến dùng được (bấm để chèn vào ô đang chọn):
              </Typography>
              <Box display="flex" flexWrap="wrap" gridGap={8}>
                {placeholders.map((p) => (
                  <Chip
                    key={p.key}
                    size="small"
                    label={p.key}
                    title={p.description}
                    onClick={() => insertPlaceholder(p.key)}
                  />
                ))}
              </Box>
            </Box>

            <Box marginTop={2}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="Tiêu đề email"
                value={subject}
                inputRef={subjectRef}
                onFocus={() => setFocused("subject")}
                onChange={(e) => setSubject(e.target.value)}
              />
            </Box>
            <Box marginTop={2}>
              <TextField
                fullWidth
                multiline
                minRows={12}
                variant="outlined"
                label="Nội dung email"
                value={content}
                inputRef={contentRef}
                onFocus={() => setFocused("content")}
                onChange={(e) => setContent(e.target.value)}
              />
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Huỷ</Button>
        <Button
          variant="contained"
          color="primary"
          disabled={loading || saving}
          onClick={handleSave}
        >
          Lưu cấu hình
        </Button>
      </DialogActions>
    </Dialog>
  );
}
